import { registerTool } from '../ToolRegistry'
import { getSharedToolRegistration } from '../sharedToolCatalog'
import { useSpellStore } from '@/stores'

const normalize = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()

export function registerSpellTools() {
  registerTool({
    ...getSharedToolRegistration('search_spells'),
    handler: async (args) => {
      try {
        const query = normalize(args?.query || '')
        if (!query) return { ok: false, error: 'Please provide a "query" to search for spells.' }

        const levelFilter =
          args?.level !== undefined && args?.level !== null ? Number(args.level) : null
        const limit = Math.max(1, Math.min(Number(args?.limit) || 10, 50))

        const spellStore = await useSpellStore()

        // Ensure spell list is loaded
        if (!Array.isArray(spellStore.spells) || spellStore.spells.length === 0) {
          await spellStore.fetchSpells?.()
        }

        const spells = Array.isArray(spellStore.spells) ? spellStore.spells : []

        const matches = []
        for (const spell of spells) {
          const name = normalize(spell?.name || spell?.title || spell?.index || '')
          const index = normalize(spell?.index || '')
          if (!name.includes(query) && !index.includes(query)) continue

          const spellLevel = spell.level !== undefined ? Number(spell.level) : null
          if (levelFilter !== null && spellLevel !== levelFilter) continue

          const school =
            spell.school && typeof spell.school === 'object'
              ? spell.school.name
              : spell.school || null

          matches.push({
            index: spell.index || index,
            name: spell.name || spell.title || spell.index,
            level: spellLevel,
            school,
            concentration: spell.concentration || false,
            ritual: spell.ritual || false,
            casting_time: spell.casting_time || spell.castingTime || null,
            range: spell.range || null
          })

          if (matches.length >= limit) break
        }

        // Sort: exact matches first, then alphabetical
        matches.sort((a, b) => {
          const aExact = normalize(a.name) === query ? 0 : 1
          const bExact = normalize(b.name) === query ? 0 : 1
          if (aExact !== bExact) return aExact - bExact
          return (a.name || '').localeCompare(b.name || '')
        })

        return { ok: true, count: matches.length, spells: matches }
      } catch (e) {
        return { ok: false, error: e?.message || 'Failed to search spells' }
      }
    }
  })

  registerTool({
    ...getSharedToolRegistration('list_spells'),
    handler: async (args) => {
      try {
        const spellStore = await useSpellStore()

        // Ensure spell list is loaded
        if (!Array.isArray(spellStore.spells) || spellStore.spells.length === 0) {
          await spellStore.fetchSpells?.()
        }

        const levelFilter = args?.level !== undefined && args?.level !== null
        const targetLevel = levelFilter ? Number(args.level) : null
        const classFilter = typeof args?.className === 'string' && args.className.trim().length > 0
        const classNeedle = classFilter ? normalize(args.className) : null
        const limit = Math.max(1, Math.min(Number(args?.limit) || 200, 200))

        const spells = Array.isArray(spellStore.spells) ? spellStore.spells : []

        const filtered = spells.filter((spell) => {
          const lvlOk = levelFilter ? Number(spell.level) === targetLevel : true
          const clsOk = classFilter
            ? Array.isArray(spell.classes)
              ? spell.classes.some(
                  (cls) => normalize(cls?.name || cls?.class?.name || cls) === classNeedle
                )
              : false
            : true
          return lvlOk && clsOk
        })

        const sorted = filtered
          .map((s) => ({
            index: s.index,
            name: s.name || s.title || s.index,
            level: s.level ?? null,
            school: s.school?.name || s.school || null,
            classes: Array.isArray(s.classes)
              ? s.classes
                  .map((c) => c?.name || c?.class?.name || (typeof c === 'string' ? c : null))
                  .filter(Boolean)
              : []
          }))
          .sort((a, b) => a.name.localeCompare(b.name))
          .slice(0, limit)
        const formatted = sorted
          .map((s, idx) => {
            const school = s.school ? ` (${s.school})` : ''
            return `${idx + 1}. ${s.name}${school}`
          })
          .join(' ')

        return { ok: true, count: sorted.length, spells: sorted, formatted }
      } catch (e) {
        return { ok: false, error: e?.message || 'Failed to list spells' }
      }
    }
  })
}
